import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {toast} from '@common-web/hooks/use-toast';
import {Button} from '@common-web/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@common-web/components/ui/card';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {trpc} from '@common-web/lib/trpc';
import {Mail, ArrowLeft, Shield, CheckCircle} from 'lucide-react';
import {AuthPageLayout} from '@common-web/components/auth-page-layout';

interface ForgotPasswordFormData {
  email: string;
}

export function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
    getValues,
  } = useForm<ForgotPasswordFormData>();

  const forgotPasswordMutation = trpc.auth.forgotPassword.useMutation();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);

    try {
      await forgotPasswordMutation.mutateAsync({
        email: data.email,
      });

      setEmailSent(true);
      toast({
        title: 'Password reset email sent',
        description: 'Check your email for instructions to reset your password',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reset email',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthPageLayout>
        <div className='w-full max-w-md'>
          <Card className='border-gray-200 shadow-xl'>
            <CardHeader className='space-y-1 pb-6'>
              <div className='flex items-center justify-center mb-4'>
                <div className='p-3 bg-green-100 rounded-full'>
                  <CheckCircle className='h-6 w-6 text-green-600' />
                </div>
              </div>
              <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
                Check Your Email
              </CardTitle>
              <CardDescription className='text-center text-brand-gray'>
                We've sent password reset instructions to{' '}
                <span className='font-medium text-brand-navy'>{getValues('email')}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                <div className='p-4 bg-blue-50 rounded-lg border border-blue-200'>
                  <p className='text-sm text-blue-800'>
                    <strong>What's next?</strong>
                    <br />
                    1. Check your email inbox (and spam folder)
                    <br />
                    2. Click the reset password link
                    <br />
                    3. Enter your new password
                  </p>
                </div>

                <div className='flex flex-col gap-3'>
                  <Button
                    onClick={() => setEmailSent(false)}
                    variant='outline'
                    className='w-full'
                  >
                    Try Different Email
                  </Button>
                  <Link to='/login'>
                    <Button variant='ghost' className='w-full'>
                      <ArrowLeft className='h-4 w-4 mr-2' />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className='text-center text-xs text-brand-gray mt-6'>
            Didn't receive the email? Check your spam folder or try again.
          </p>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className='w-full max-w-md'>
        <Card className='border-gray-200 shadow-xl'>
          <CardHeader className='space-y-1 pb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='p-3 bg-brand-navy/10 rounded-full'>
                <Shield className='h-6 w-6 text-brand-navy' />
              </div>
            </div>
            <CardTitle className='text-2xl text-center font-bold text-brand-navy'>
              Forgot Your Password?
            </CardTitle>
            <CardDescription className='text-center text-brand-gray'>
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='flex flex-col gap-5'>
                <div className='space-y-2'>
                  <Label htmlFor='email' className='text-brand-navy font-medium'>
                    Email Address
                  </Label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                    <Input
                      id='email'
                      type='email'
                      placeholder='you@example.com'
                      className='pl-10 h-11 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                      disabled={isLoading}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className='text-sm text-red-600 mt-1'>{errors.email.message}</p>
                  )}
                </div>

                <Button
                  type='submit'
                  className='w-full h-11 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold transition-colors shadow-sm'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className='flex items-center gap-2'>
                      <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                      Sending Email...
                    </span>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </div>

              <div className='mt-6 text-center'>
                <Link
                  to='/login'
                  className='text-sm text-brand-blue hover:text-brand-blue-dark font-semibold transition-colors inline-flex items-center gap-1'
                >
                  <ArrowLeft className='h-3 w-3' />
                  Back to Sign In
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className='text-center text-xs text-brand-gray mt-6'>
          Protected by enterprise-grade security. Your data is encrypted and secure.
        </p>
      </div>
    </AuthPageLayout>
  );
}
